import { create } from 'zustand';

/** Someone who stopped typing without saying so (closed the app, lost signal) is forgotten after this long. */
export const TYPING_EXPIRES_MS = 6_000;

type Typers = Record<string, Record<string, string>>;

interface TypingState {
  /** Who is typing: conversation id → member id → their name. */
  typers: Typers;
  typingStart: (conversationId: string, memberId: string, name: string) => void;
  typingStop: (conversationId: string, memberId: string) => void;
  /** Forgets everyone (sign-out). */
  clear: () => void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();
const timerKey = (conversationId: string, memberId: string) => `${conversationId}:${memberId}`;

/** "typing…": who is typing in which chat, for the chat and the chat list. Nothing is kept for long. */
export const useTypingStore = create<TypingState>()((set, get) => ({
  typers: {},

  typingStart: (conversationId, memberId, name) => {
    const key = timerKey(conversationId, memberId);
    clearTimeout(timers.get(key));
    timers.set(
      key,
      setTimeout(() => get().typingStop(conversationId, memberId), TYPING_EXPIRES_MS),
    );
    set((state) => ({
      typers: {
        ...state.typers,
        [conversationId]: { ...state.typers[conversationId], [memberId]: name },
      },
    }));
  },

  typingStop: (conversationId, memberId) => {
    const key = timerKey(conversationId, memberId);
    clearTimeout(timers.get(key));
    timers.delete(key);
    set((state) => {
      const chat = Object.fromEntries(
        Object.entries(state.typers[conversationId] ?? {}).filter(([id]) => id !== memberId),
      );
      const typers = Object.fromEntries(
        Object.entries(state.typers).filter(([id]) => id !== conversationId),
      );
      if (Object.keys(chat).length > 0) typers[conversationId] = chat;
      return { typers };
    });
  },

  clear: () => {
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    set({ typers: {} });
  },
}));

/** The names of the people typing in a chat. */
export function namesTyping(typers: Typers, conversationId: string): string[] {
  return Object.values(typers[conversationId] ?? {});
}
